import { useEffect, useState } from 'react';
import { Loader2, Save, Check, AlertTriangle } from 'lucide-react';
import { useUnsavedStore } from '@/stores/unsavedDrafts.store';
import styles from './UnsavedChangesBar.module.scss';

/**
 * Barre flottante en bas : liste les contenus édités mais NON ENREGISTRÉS
 * et permet de tout enregistrer (sur disque) d'un coup.
 */
export function UnsavedChangesBar() {
  const drafts = useUnsavedStore((s) => s.drafts);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const list = Object.values(drafts);
  const total = list.length;

  // Avertit avant fermeture/rafraîchissement de l'onglet si des modifs ne sont pas enregistrées.
  useEffect(() => {
    if (total === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [total]);

  if (total === 0 && !feedback) return null;

  const saveAll = async () => {
    setSaving(true);
    setFeedback(null);
    let ok = 0;
    let fail = 0;
    // Copie figée : chaque save() réussie retire son brouillon du store en cours de route.
    for (const d of [...list]) {
      try {
        (await d.save()) ? ok++ : fail++;
      } catch {
        fail++;
      }
    }
    setSaving(false);
    setFeedback(
      fail === 0
        ? { ok: true, msg: `${ok} contenu${ok > 1 ? 's' : ''} enregistré${ok > 1 ? 's' : ''}.` }
        : { ok: false, msg: `${ok} enregistré(s), ${fail} en échec` },
    );
    if (fail === 0) setTimeout(() => setFeedback(null), 4000);
  };

  return (
    <div className={styles.bar}>
      {total > 0 ? (
        <>
          <span className={styles.count}>
            <span className={styles.dot} />
            {total} contenu{total > 1 ? 's' : ''} modifié{total > 1 ? 's' : ''} non enregistré{total > 1 ? 's' : ''}
            <span className={styles.items}>{list.map((d) => d.label).join(' · ')}</span>
          </span>
          <button className={styles.saveBtn} onClick={saveAll} disabled={saving}>
            {saving ? <Loader2 size={15} className={styles.spin} /> : <Save size={15} />}
            {saving ? 'Enregistrement…' : 'Enregistrer tout'}
          </button>
        </>
      ) : feedback ? (
        <span className={feedback.ok ? styles.okMsg : styles.errMsg}>
          {feedback.ok ? <Check size={15} /> : <AlertTriangle size={15} />} {feedback.msg}
        </span>
      ) : null}
    </div>
  );
}
